import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, AlertTriangle, Pause, Play } from 'lucide-react';
import { api } from '../../api/client';
import type { Tenant } from '../../types/domain';
import { LicensesTab } from './tabs/LicensesTab';
import { UsersTab } from './tabs/UsersTab';
import { OverviewTab } from './tabs/OverviewTab';
import { LocationsTab } from './tabs/LocationsTab';
import { CertificatesTab } from './tabs/CertificatesTab';
import { SessionsTab } from './tabs/SessionsTab';
import { ReferralTab } from './tabs/ReferralTab';
import { AffiliateTab } from './tabs/AffiliateTab';
import { LegalTab } from './tabs/LegalTab';
import { ContractsTab } from './tabs/ContractsTab';
import { ImpersonationTab } from './tabs/ImpersonationTab';
import { ConfirmDialog } from '../../components/ConfirmDialog';

type Tab = 'overview' | 'users' | 'licenses' | 'locations' | 'certificates' | 'sessions' | 'referral' | 'affiliate' | 'legal' | 'contracts' | 'support';

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [error, setError] = useState<string | null>(null);
  const [showSuspendDialog, setShowSuspendDialog] = useState(false);
  const [showUnsuspendDialog, setShowUnsuspendDialog] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    try {
      const t = await api.get<Tenant>(`/admin/tenants/${id}`);
      setTenant(t);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!tenant) {
    return (
      <div>
        {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
        {!error && <div style={{ color: 'var(--color-text-muted)' }}>Učitavanje...</div>}
      </div>
    );
  }

  const handleSuspendConfirm = async (reason?: string) => {
    if (!tenant) return;
    await api.post(`/admin/tenants/${tenant.id}/suspend`, { reason });
    refresh();
  };

  const handleUnsuspendConfirm = async () => {
    if (!tenant) return;
    await api.post(`/admin/tenants/${tenant.id}/unsuspend`);
    refresh();
  };

  return (
    <div>
      <Link to="/tenants" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, textDecoration: 'none' }}>
        <ChevronLeft size={14} /> Tenanti
      </Link>

      <header style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {tenant.name}
            {tenant.suspended && <span style={{ marginLeft: 12, fontSize: 12, padding: '4px 10px', borderRadius: 12, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontWeight: 600 }}>SUSPENDOVANO</span>}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            PIB: {tenant.taxId || '-'}
            {tenant.contactEmail && <> · {tenant.contactEmail}</>}
          </div>
        </div>
        {tenant.suspended ? (
          <button onClick={() => setShowUnsuspendDialog(true)} style={btnPrimary}><Play size={14} /> Otključaj</button>
        ) : (
          <button onClick={() => setShowSuspendDialog(true)} style={btnDanger}><Pause size={14} /> Suspenduj</button>
        )}
      </header>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--color-border)', marginBottom: 20, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {(['overview', 'users', 'licenses', 'locations', 'certificates', 'sessions', 'referral', 'affiliate', 'legal', 'contracts', 'support'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: '10px 18px', border: 'none', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap',
              background: 'transparent',
              color: tab === t ? 'var(--color-text-strong)' : 'var(--color-text-muted)',
              fontSize: 13, fontWeight: tab === t ? 600 : 500,
              borderBottom: tab === t ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {{ overview: 'Pregled', users: 'Korisnici', licenses: 'Licence', locations: 'Lokacije', certificates: 'Sertifikati', sessions: 'Sesije', referral: 'Referrali', affiliate: 'Partnerske ponude', legal: 'Uslovi', contracts: 'Ugovori', support: 'Pristup podrške' }[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab tenant={tenant} />}
      {tab === 'users' && <UsersTab tenantId={tenant.id} />}
      {tab === 'licenses' && <LicensesTab tenantId={tenant.id} />}
      {tab === 'locations' && <LocationsTab tenantId={tenant.id} />}
      {tab === 'certificates' && <CertificatesTab tenantId={tenant.id} />}
      {tab === 'sessions' && <SessionsTab tenantId={tenant.id} />}
      {tab === 'referral' && <ReferralTab tenantId={tenant.id} />}
      {tab === 'affiliate' && <AffiliateTab tenantId={tenant.id} />}
      {tab === 'legal' && <LegalTab tenantId={tenant.id} />}
      {tab === 'contracts' && <ContractsTab tenantId={tenant.id} />}
      {tab === 'support' && <ImpersonationTab tenantId={tenant.id} />}

      {showSuspendDialog && (
        <ConfirmDialog
          title="Suspenduj tenant"
          message={`Da li želite da suspendujete tenant "${tenant.name}"? Korisnici neće moći da se loguju dok ga ne otkljucate.`}
          confirmLabel="Suspenduj"
          variant="danger"
          input={{
            label: 'Razlog suspenzije',
            placeholder: 'npr. Neplaćena faktura',
            required: true,
            multiline: true,
          }}
          onConfirm={handleSuspendConfirm}
          onClose={() => setShowSuspendDialog(false)}
        />
      )}

      {showUnsuspendDialog && (
        <ConfirmDialog
          title="Otključaj tenant"
          message={`Da li želite da otkljucate tenant "${tenant.name}"? Korisnici će moći ponovo da se loguju.`}
          confirmLabel="Otključaj"
          variant="default"
          onConfirm={handleUnsuspendConfirm}
          onClose={() => setShowUnsuspendDialog(false)}
        />
      )}
    </div>
  );
}

const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnDanger: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-danger)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13 };
